// =====================================================================
//  The Deck Builder's helper: what a deck still needs, and cards that fit it.
//
//  "Needs" compares the deck with the shape Random Deck aims for (RANDOM_DECK in randomDeck.ts): how many
//  Troops, Spells, Support cards and Buildings, and how much of it is cheap, medium and heavy, nudged by the
//  archetype the deck looks most like. "Suggestions" scores every card the deck can still take by how well
//  it matches the deck's affiliations and archetypes and how much it fills what is short.
// =====================================================================
import { isEmperor, isSecretRare, type CardDb, type CardDef } from './cards.ts';
import { deckStats, whyCantAdd, MAIN_DECK_SIZE, type Deck } from './deck.ts';
import { ARCHETYPES, bandOf, guessArchetypes, leaningsOf, profileDeck } from './archetypes.ts';
import { RANDOM_DECK } from './randomDeck.ts';

export const ADVICE = {
  // a need is shown as short once the deck is this many cards under where it should be by now
  shortBy: 3,
  // …and as too many once it is this far over what a full deck wants
  overBy: 6,
  // how much each thing counts toward a suggestion
  weight: { affiliation: 3, neutral: 1.2, archetype: 2, typeGap: 2.5, curveGap: 1.5, moreCopies: 0.8 },
};

const SUPPORT = ['biome', 'tome', 'trigger'];
export const KIND_NAMES: Record<string, string> = { troop: 'Troops', spell: 'Spells', support: 'Support', building: 'Buildings' };
export const BAND_NAMES: Record<string, string> = { cheap: 'Cheap (1–3)', medium: 'Medium (4–8)', heavy: 'Heavy (9+)' };

export const kindOf = (c: CardDef) =>
  c.types.includes('troop') ? 'troop' : c.types.includes('building') ? 'building' : c.types.includes('spell') ? 'spell' : c.types.some((t) => SUPPORT.includes(t)) ? 'support' : null;
export const bandKey = (c: CardDef) => (c.cost === null ? null : bandOf(c.cost).id === 'cheap' ? 'cheap' : bandOf(c.cost).id === 'medium' ? 'medium' : 'heavy');

export interface Need {
  id: string;
  label: string; // "Troops"
  have: number;
  aim: number; // for a full deck
  state: 'ok' | 'short' | 'over' | 'missing';
  hint: string; // one line for the tooltip
}
export interface DeckAdvice {
  needs: Need[];
  archetype: string | null; // the archetype the aims follow
  affiliations: string[]; // the deck's main affiliations, most first (Neutral left out)
}

/** The archetypes the deck leans toward most: its best guesses once it is big enough, else its leaning cards. */
function topArchetypes(deck: Deck, db: CardDb, aimFor: string[] | null = null): string[] {
  // a deck laid out by Random Deck keeps aiming for the archetypes it was built around
  if (aimFor?.length) return aimFor.slice(0, 2);
  const p = profileDeck(deck, db);
  const guessed = guessArchetypes(p).filter((f) => f.fit >= 35).map((f) => f.id);
  if (guessed.length) return guessed.slice(0, 2);
  return Object.entries(p.leans).filter(([, s]) => s >= 0.12).sort((a, b) => b[1] - a[1]).slice(0, 2).map(([id]) => id);
}

function affiliationsOf(deck: Deck, db: CardDb): string[] {
  const by = new Map<string, number>();
  for (const [num, n] of deck) {
    const c = db.byNumber.get(num);
    if (!c || isEmperor(c)) continue;
    for (const a of c.affiliations) if (a !== 'neutral') by.set(a, (by.get(a) ?? 0) + n);
  }
  // the Emperor's affiliation counts as a lot: the deck is built around it
  for (const [num] of deck) {
    const c = db.byNumber.get(num);
    if (c && isEmperor(c)) for (const a of c.affiliations) if (a !== 'neutral') by.set(a, (by.get(a) ?? 0) + 8);
  }
  const total = [...by.values()].reduce((a, b) => a + b, 0);
  return [...by].sort((a, b) => b[1] - a[1]).filter(([, n], i) => i === 0 || n >= total * 0.2).slice(0, 2).map(([a]) => a);
}

function aims(archetype: string | null, second: string | null = null) {
  const t = RANDOM_DECK.types;
  const shift = (archetype && RANDOM_DECK.archetypeTypes[archetype]) || {};
  // (a second archetype counts for half, as in Random Deck: only for a deck Random Deck laid out)
  const shift2 = (second && RANDOM_DECK.archetypeTypes[second]) || {};
  const typeAim = (k: string) => Math.max(0, Math.round((t[k] ?? 0) + (shift[k] ?? 0) + (shift2[k] ?? 0) / 2));
  const kinds: Record<string, number> = {
    troop: typeAim('troop'),
    spell: typeAim('spell'),
    support: typeAim('biome') + typeAim('tome') + typeAim('trigger'),
    building: typeAim('building'),
  };
  // whatever is left of the 60 goes to Troops and Spells, as Random Deck does it
  const rest = MAIN_DECK_SIZE - Object.values(kinds).reduce((a, b) => a + b, 0);
  kinds.troop += Math.round(rest * RANDOM_DECK.restSplit);
  kinds.spell += rest - Math.round(rest * RANDOM_DECK.restSplit);
  const curve = (archetype && RANDOM_DECK.archetypeCurve[archetype]) || RANDOM_DECK.curve;
  const bands: Record<string, number> = {};
  for (const b of ['cheap', 'medium', 'heavy']) bands[b] = Math.round((curve[b] ?? 0) * MAIN_DECK_SIZE);
  return { kinds, bands };
}

/** `aimFor`: the archetypes the deck was built around (Random Deck's theme), when there are any: the aims
 *  follow them rather than the archetype the deck's cards look most like. */
export function adviseDeck(deck: Deck, db: CardDb, aimFor: string[] | null = null): DeckAdvice {
  const { emperors, main } = deckStats(deck, db);
  const archs = topArchetypes(deck, db, aimFor);
  const arch = archs[0] ?? null;
  const { kinds, bands } = aims(arch, aimFor?.length ? (archs[1] ?? null) : null);
  const have = { kinds: {} as Record<string, number>, bands: {} as Record<string, number> };
  for (const [num, n] of deck) {
    const c = db.byNumber.get(num);
    if (!c || isEmperor(c)) continue;
    const k = kindOf(c);
    if (k) have.kinds[k] = (have.kinds[k] ?? 0) + n;
    const b = bandKey(c);
    if (b) have.bands[b] = (have.bands[b] ?? 0) + n;
  }
  const needs: Need[] = [];
  needs.push({
    id: 'emperor', label: 'Emperor', have: emperors, aim: 1,
    state: emperors === 1 ? 'ok' : emperors === 0 ? 'missing' : 'over',
    hint: emperors === 1 ? 'Your deck has its Emperor.' : emperors === 0 ? 'Every deck needs one Emperor.' : 'A deck holds only one Emperor.',
  });
  needs.push({
    id: 'cards', label: 'Cards', have: main, aim: MAIN_DECK_SIZE,
    state: main === MAIN_DECK_SIZE ? 'ok' : main > MAIN_DECK_SIZE ? 'over' : 'short',
    hint: main === MAIN_DECK_SIZE ? 'All 60 cards are in.' : main > MAIN_DECK_SIZE ? `${main - MAIN_DECK_SIZE} too many: take some out.` : `${MAIN_DECK_SIZE - main} more to go.`,
  });
  // where the deck should be by now: the aim, scaled to how full it is
  const fill = Math.min(1, main / MAIN_DECK_SIZE);
  const judge = (id: string, label: string, h: number, aim: number, what: string): Need => {
    const byNow = aim * fill;
    const state = h > aim + ADVICE.overBy ? 'over' : main >= 10 && h < byNow - ADVICE.shortBy ? 'short' : 'ok';
    const hint =
      state === 'short' ? `Low on ${what}: ${h} so far, a full deck wants about ${aim}.`
      : state === 'over' ? `Lots of ${what}: ${h}, a full deck wants about ${aim}.`
      : `${h} ${what}; a full deck wants about ${aim}.`;
    return { id, label, have: h, aim, state, hint };
  };
  for (const k of ['troop', 'spell', 'support', 'building']) needs.push(judge(k, KIND_NAMES[k], have.kinds[k] ?? 0, kinds[k], KIND_NAMES[k].toLowerCase() + (k === 'support' ? ' cards' : '')));
  for (const b of ['cheap', 'medium', 'heavy']) needs.push(judge(b, BAND_NAMES[b], have.bands[b] ?? 0, bands[b], `${b} cards`));
  return { needs, archetype: arch, affiliations: affiliationsOf(deck, db) };
}

export interface Suggestion {
  card: CardDef;
  score: number;
  why: string[]; // short reasons: "Duned", "leans Aggro", "you need Spells"
}

/** The cards that fit the deck best, best first. Empty for an empty or full deck. */
export function suggestCards(deck: Deck, db: CardDb, count = 10, aimFor: string[] | null = null): Suggestion[] {
  const { emperors, main } = deckStats(deck, db);
  if (main + emperors === 0) return [];
  const advice = adviseDeck(deck, db, aimFor);
  const affs = advice.affiliations;
  const archs = topArchetypes(deck, db, aimFor);
  const archName = (id: string) => ARCHETYPES.find((a) => a.id === id)?.name ?? id;
  const W = ADVICE.weight;
  const gap = (id: string) => {
    const n = advice.needs.find((x) => x.id === id);
    if (!n || n.aim === 0) return 0;
    const byNow = n.aim * Math.min(1, (main + 1) / MAIN_DECK_SIZE);
    return Math.max(0, Math.min(1, (byNow - n.have) / Math.max(4, n.aim * 0.4)));
  };
  const wantEmperor = emperors === 0;
  if (!wantEmperor && main >= MAIN_DECK_SIZE) return [];
  const out: Suggestion[] = [];
  // The secret rares only work together: with one of them in the deck, the other two come first, ahead of
  // everything else (even of the Emperors, while the deck has none), and so do more Calling Pillars until there
  // are enough to fill every lane.
  const secrets = db.list.filter(isSecretRare);
  const hasSecret = secrets.find((c) => (deck.get(c.number) ?? 0) > 0);
  const PILLARS_NEEDED = 5;
  const partner = (c: CardDef): { score: number; why: string } | null => {
    if (!hasSecret || !isSecretRare(c)) return null;
    const have = deck.get(c.number) ?? 0;
    if (have === 0) return { score: 1000, why: `needed with ${hasSecret.name}` };
    if (c.whileInPlay.includes('calling') && have < PILLARS_NEEDED) return { score: 500, why: `one for each lane: ${have} of ${PILLARS_NEEDED}` };
    return null;
  };
  for (const c of db.list) {
    if (c.spawnOnly || c.types.includes('unassigned') || whyCantAdd(deck, c, db)) continue;
    const must = partner(c);
    if (isEmperor(c) !== wantEmperor && !must) continue;
    const why: string[] = [];
    let score = 0;
    if (must) {
      score += must.score;
      why.push(must.why);
    }
    const shared = c.affiliations.filter((a) => affs.includes(a));
    if (shared.length) {
      score += W.affiliation * (affs.indexOf(shared[0]) === 0 ? 1 : 0.7);
      why.push(shared[0].replace(/\b\w/g, (x) => x.toUpperCase()));
    } else if (c.affiliations.includes('neutral')) score += W.neutral;
    else if (affs.length) score -= 1.5;
    if (!wantEmperor || must) {
      const leans = [...leaningsOf(c).keys()].filter((id) => archs.includes(id));
      if (leans.length) {
        score += W.archetype * (leans.includes(archs[0]) ? 1 : 0.6);
        why.push(`leans ${archName(leans[0])}`);
      }
      const k = kindOf(c);
      const tg = k ? gap(k) : 0;
      if (tg > 0) {
        score += W.typeGap * tg;
        if (tg > 0.3) why.push(`you need ${KIND_NAMES[k!]}`);
      }
      const b = bandKey(c);
      const cg = b ? gap(b) : 0;
      if (cg > 0) {
        score += W.curveGap * cg;
        if (cg > 0.3) why.push(`you need ${b} cards`);
      }
      if ((deck.get(c.number) ?? 0) > 0) {
        score += W.moreCopies;
        why.push('another copy');
      }
    }
    out.push({ card: c, score, why });
  }
  return out.sort((a, b) => b.score - a.score || a.card.number - b.card.number).slice(0, count);
}
